import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Dropdown from '../components/common/Dropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCheckPromoCodeMutation,
  useCreateSetupIntentMutation,
} from '../redux/apiSlice';
import { toast } from 'react-toastify';
import { CircleChevronLeft, Loader2 } from 'lucide-react';
import TermsConditions from './TermsConditions';
import LoginDetailsModal from '../components/common/LoginDetailsModal';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
);

const PaymentForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriptionType, userDetails } = location.state || {};
  console.log('userDetails', subscriptionType, userDetails);

  const stripe = useStripe();
  const elements = useElements();
  const [formData, setFormData] = useState({
    email: userDetails?.user?.email || '',
    cardHolderName: '',
    country: '',
    postalCode: '',
  });
  const [isCredModalOpen, setIsCredModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [
    createSetupIntent,
    { isLoading: setupIntentLoading, error, isSuccess },
  ] = useCreateSetupIntentMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange =
    (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      toast.error('Please enter your card details.');
      return;
    }

    setIsLoading(true);

    try {
      // const { error: paymentMethodError, paymentMethod } =
      //   await stripe.createPaymentMethod({
      //     type: 'card',
      //     card: cardElement!,
      //     billing_details: {
      //       email: formData.email,
      //       name: formData.cardHolderName,
      //     },
      //   });

      // if (paymentMethodError) {
      //   console.error('Payment Method Error:', paymentMethodError.message);
      //   toast.error(
      //     paymentMethodError.message || 'Failed to create payment method.',
      //   );
      //   setIsLoading(false);
      //   return;
      // }

      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        toast.error(error.message || 'Failed to create payment method.');
        return;
      }

      const response: any = await createSetupIntent({
        user: userDetails.user.id,
        paymentMethodId: token?.id,
        email: formData.email,
        name: formData.cardHolderName,
        subscriptionType: subscriptionType,
      }).unwrap();

      if (response?.paymentMethodId) {
        toast.success(response?.message);
        setIsCredModalOpen(true);
      } else {
        toast.error('Payment was not successful. Please try again.');
      }
    } catch (error) {
      console.error('Unexpected Error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false); // Reset loading state in finally
    }
  };

  const formStyles = 'mb-4';

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'UK', label: 'United Kingdom' },
  ];
  return (
    <>
      {isCredModalOpen && (
        <LoginDetailsModal
          isOpen={true}
          onClose={() => {
            navigate('/login');
          }}
          username={userDetails?.user?.email}
          password={userDetails?.password}
          firstName={userDetails?.user?.name}
        />
      )}
      <form onSubmit={handleSubmit}>
        <div className={formStyles}>
          <label htmlFor="email" className="text-xs block font-medium">
            Email
          </label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="jane.diaz@example.com"
            disabled={true}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="cardHolderName" className="text-xs block font-medium">
            Name on card
          </label>
          <Input
            type="text"
            id="cardHolderName"
            name="cardHolderName"
            value={formData.cardHolderName}
            onChange={handleChange}
            placeholder="Jane Diaz"
            disabled={false}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="card" className="text-xs block font-medium">
            Card Information
          </label>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#333',
                  '::placeholder': {
                    color: '#aaa',
                  },
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a',
                },
              },
            }}
          />
        </div>
        <div className={formStyles}>
          <label htmlFor="country" className="text-xs block font-medium">
            Country or region
          </label>
          <Dropdown
            id="country"
            name="country"
            options={countryOptions}
            value={formData.country}
            onSelect={handleSelectChange('country')}
            placeholder="Select country"
            disabled={false}
          />
        </div>
        {/* <div className={formStyles}>
          <label htmlFor="postalCode" className="text-xs block font-medium">
            Postal Code
          </label>
          <Input
            type="text"
            id="postalCode"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="97712"
            disabled={false}
          />
        </div> */}
        <Button
          type="submit"
          className="w-full text-[24px] mt-4"
          disabled={!stripe || isLoading}
        >
          {isLoading ? (
            <Loader2 className="animate-spin h-6 w-6 m-auto" />
          ) : (
            'Confirm'
          )}
        </Button>
      </form>
    </>
  );
};

const PaymentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex justify-center min-h-screen mt-10">
      <div className="flex bg-white p-8 shadow-[0_0_15px_rgba(0,0,0,0.2)] max-w-5xl w-full rounded-lg h-fit">
        <div className="w-full pr-8 h-auto">
          <div className="flex items-center mb-8">
            <CircleChevronLeft
              onClick={() => navigate(-1)}
              className="w-8 h-8 mr-4 text-gray-600 cursor-pointer hover:text-gray-800"
            />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-700">
              Checkout
            </h1>
          </div>
          <Elements stripe={stripePromise}>
            <PaymentForm />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
